'use client'

import { Button, Modal } from 'antd'
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  clearCapturedInstallPrompt,
  getCapturedInstallPrompt,
  subscribeInstallPrompt,
  type BeforeInstallPromptEvent,
} from '@/lib/pwa/captureInstallPrompt'
import { PwaInstallContext } from './pwaInstallContext'
import styles from './styles.module.scss'

const DISMISS_KEY = 'pwa-install-dismissed-at'
const DISMISS_DAYS = 7

function isDismissedRecently() {
  const dismissedAt = localStorage.getItem(DISMISS_KEY)
  if (!dismissedAt) {
    return false
  }

  const elapsed = Date.now() - Number(dismissedAt)
  return elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000
}

function isStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

function isIosDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream
}

function getInstallPrompt(
  statePrompt: BeforeInstallPromptEvent | null,
  refPrompt: BeforeInstallPromptEvent | null,
) {
  return statePrompt ?? refPrompt ?? getCapturedInstallPrompt()
}

type PwaInstallProviderProps = {
  children: ReactNode
  showBanner?: boolean
}

export function PwaInstallProvider({ children, showBanner = true }: PwaInstallProviderProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [visible, setVisible] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [isIos, setIsIos] = useState(false)
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    if (isStandaloneMode()) {
      return
    }

    const ios = isIosDevice()
    setIsIos(ios)

    const dismissed = isDismissedRecently()
    const canShowBanner = showBanner && !dismissed

    const onPrompt = (promptEvent: BeforeInstallPromptEvent) => {
      deferredPromptRef.current = promptEvent
      setDeferredPrompt(promptEvent)
      if (canShowBanner) {
        setVisible(true)
      }
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      onPrompt(event as BeforeInstallPromptEvent)
    }

    const capturedPrompt = getCapturedInstallPrompt()
    if (capturedPrompt) {
      onPrompt(capturedPrompt)
    }

    const unsubscribe = subscribeInstallPrompt(onPrompt)
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    if (ios && canShowBanner) {
      setVisible(true)
    }

    return () => {
      unsubscribe()
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [showBanner])

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setVisible(false)
    setDetailOpen(false)
  }, [])

  const closeDetail = useCallback(() => {
    setDetailOpen(false)
  }, [])

  const handleInstall = useCallback(async () => {
    const prompt = getInstallPrompt(deferredPrompt, deferredPromptRef.current)
    if (!prompt) {
      return
    }

    await prompt.prompt()
    const choice = await prompt.userChoice

    if (choice.outcome === 'accepted') {
      setVisible(false)
    }

    deferredPromptRef.current = null
    setDeferredPrompt(null)
    clearCapturedInstallPrompt()
  }, [deferredPrompt])

  const runInstallRef = useRef<() => void>(() => {})

  runInstallRef.current = () => {
    if (isIosDevice()) {
      setDetailOpen(true)
      return
    }

    const prompt = getInstallPrompt(deferredPrompt, deferredPromptRef.current)
    if (!prompt) {
      return
    }

    void prompt.prompt().then(async () => {
      const choice = await prompt.userChoice

      if (choice.outcome === 'accepted') {
        setVisible(false)
      }

      deferredPromptRef.current = null
      setDeferredPrompt(null)
      clearCapturedInstallPrompt()
    })
  }

  const runInstall = useCallback(() => {
    runInstallRef.current()
  }, [])

  const contextValue = useMemo(() => ({ runInstall }), [runInstall])

  return (
    <PwaInstallContext.Provider value={contextValue}>
      {children}

      {visible && showBanner && (
        <div className={styles.banner} role="dialog" aria-label="Cài đặt ứng dụng TravelEasy">
          <button type="button" className={styles.close} onClick={dismiss} aria-label="Đóng">
            <CloseOutlined />
          </button>

          <div className={styles.content}>
            <img src="/icon/home-page/te.png" alt="" width={44} height={44} className={styles.icon} />
            <div className={styles.text}>
              <strong>Cài TravelEasy</strong>
              <p>
                {isIos
                  ? 'Nhấn Chia sẻ rồi chọn "Thêm vào Màn hình chính" để dùng như app.'
                  : 'Thêm vào màn hình chính để trải nghiệm nhanh hơn.'}
              </p>
            </div>
          </div>

          {isIos ? (
            <Button type="primary" block onClick={() => setDetailOpen(true)}>
              Xem chi tiết
            </Button>
          ) : (
            deferredPrompt && (
              <Button type="primary" block icon={<DownloadOutlined />} onClick={() => void handleInstall()}>
                Cài đặt ngay
              </Button>
            )
          )}
        </div>
      )}

      {isIos && (
        <Modal
          open={detailOpen}
          onCancel={closeDetail}
          centered
          width={360}
          zIndex={1300}
          className={styles.modal}
          title="Hướng dẫn cài TravelEasy"
          footer={
            <Button type="primary" block onClick={closeDetail}>
              Đã hiểu
            </Button>
          }
          destroyOnHidden
        >
          <p className={styles.modalDesc}>
            Thêm vào Màn hình chính để mở nhanh như app.
          </p>
          <ol className={styles.iosSteps}>
            <strong>Bước 1:</strong> Nhấn vào nút Chia sẻ (biểu tượng hình vuông có mũi tên lên) ở thanh công cụ phía dưới màn hình.<br />
            <strong>Bước 2:</strong> Cuộn menu xuống dưới và chọn &quot;Thêm vào Màn hình chính&quot; (Add to Home Screen).<br />
            <strong>Bước 3:</strong> (Tùy chọn) Đổi lại tên hiển thị của ứng dụng cho ngắn gọn, sau đó nhấn Thêm (Add) ở góc trên bên phải để hoàn tất.<br />
          </ol>
        </Modal>
      )}
    </PwaInstallContext.Provider>
  )
}

export default PwaInstallProvider
